class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this._onOverlayClick = this.close.bind(this);
  }

  connectedCallback() {
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay || overlay._vrvOverlayBound) return;
    overlay._vrvOverlayBound = true;
    overlay.addEventListener('click', this._onOverlayClick);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-controls', 'CartDrawer');

    if (cartLink._vrvCartDrawerClick) {
      cartLink.removeEventListener('click', cartLink._vrvCartDrawerClick);
    }

    cartLink._vrvCartDrawerClick = (event) => {
      event.preventDefault();
      this.open(cartLink);
    };

    cartLink.addEventListener('click', cartLink._vrvCartDrawerClick);

    if (cartLink._vrvCartDrawerKeydown) {
      cartLink.removeEventListener('keydown', cartLink._vrvCartDrawerKeydown);
    }

    cartLink._vrvCartDrawerKeydown = (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    };

    cartLink.addEventListener('keydown', cartLink._vrvCartDrawerKeydown);

    if (cartLink._vrvCartDrawerPrefetch) {
      cartLink.removeEventListener('mouseenter', cartLink._vrvCartDrawerPrefetch);
      cartLink.removeEventListener('focus', cartLink._vrvCartDrawerPrefetch);
    }

    cartLink._vrvCartDrawerPrefetch = () => {
      const last = this._vrvLastRefreshAt || 0;
      if (Date.now() - last < 5000 || this._vrvPrefetchInflight) return;
      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      this._vrvPrefetchInflight = fetch(`${cartUrl}?section_id=cart-drawer`, {
        method: 'GET',
        headers: { Accept: 'text/html' },
        cache: 'no-store',
        credentials: 'same-origin',
      })
        .then((response) => (response.ok ? response.text() : null))
        .then((responseText) => {
          if (!responseText) return;
          this._vrvPrefetchedDrawerHtml = responseText;
          this._vrvPrefetchedAt = Date.now();
        })
        .catch(() => null)
        .finally(() => {
          this._vrvPrefetchInflight = null;
        });
    };

    cartLink.addEventListener('mouseenter', cartLink._vrvCartDrawerPrefetch, { passive: true });
    cartLink.addEventListener('focus', cartLink._vrvCartDrawerPrefetch, { passive: true });
  }

  open(triggeredBy, options) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    const lastRefresh = this._vrvLastRefreshAt || 0;
    const isRecentlyRefreshed = Date.now() - lastRefresh < 3500;
    const skipRefresh = Boolean(options && options.skipRefresh);

    // Refresh drawer with fresh server HTML on open so newly added items appear immediately (BUG_282),
    // but avoid redundant in-flight re-fetch if we just refreshed/rendered milliseconds ago.
    // Not forced: a drawer HTML prefetched on icon hover/focus (< 5s old) is
    // used instead of fetching the same section a second time.
    if (!skipRefresh && !isRecentlyRefreshed && typeof this.refreshDrawer === 'function') {
      this.refreshDrawer(false);
    }
    if (window.vrvUserSync && typeof window.vrvUserSync.syncOnDrawerOpen === 'function') {
      window.vrvUserSync.syncOnDrawerOpen();
    }
    // requestAnimationFrame triggers the open animation reliably without an extra timer delay
    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Contents just changed; an older prefetched copy of the drawer is stale.
    this.clearPrefetchCache();
    this.syncEmptyState(parsedState);

    if (parsedState.sections && parsedState.sections['cart-drawer']) {
      const doc = new DOMParser().parseFromString(parsedState.sections['cart-drawer'], 'text/html');
      const sourceInner = doc.querySelector('#CartDrawer .drawer__inner');
      const liveInner = this.querySelector('#CartDrawer .drawer__inner');

      if (sourceInner && liveInner) {
        liveInner.innerHTML = sourceInner.innerHTML;
        if (this.classList.contains('is-coupons-open')) {
          const couponsPage = this.querySelector('.vrv-cart-drawer__coupons-page');
          if (couponsPage) {
            couponsPage.removeAttribute('hidden');
            couponsPage.setAttribute('aria-hidden', 'false');
            couponsPage.classList.add('is-open');
          }
        }
      } else {
        const cartDrawerEl = document.getElementById('CartDrawer');
        if (cartDrawerEl && parsedState.sections['cart-drawer']) {
          cartDrawerEl.innerHTML = this.getSectionInnerHTML(parsedState.sections['cart-drawer'], '#CartDrawer');
        }
      }
    }

    if (parsedState.sections && parsedState.sections['cart-icon-bubble']) {
      const iconEl = document.getElementById('cart-icon-bubble');
      if (iconEl) {
        iconEl.innerHTML = this.getSectionInnerHTML(parsedState.sections['cart-icon-bubble'], '.shopify-section');
        this.setHeaderCartIconAccessibility();
      }
    }

    this._vrvLastRefreshAt = Date.now();

    setTimeout(() => {
      this.bindOverlay();
      this.setHeaderCartIconAccessibility();
    });
  }

  syncEmptyState(parsedState) {
    const count = parsedState.item_count;
    if (typeof count === 'number') {
      this.classList.toggle('is-empty', count === 0);
      if (count === 0) return;
    }

    const drawerSectionHtml = parsedState.sections && parsedState.sections['cart-drawer'];
    if (drawerSectionHtml) {
      const doc = new DOMParser().parseFromString(drawerSectionHtml, 'text/html');
      const sourceDrawer = doc.querySelector('cart-drawer');
      if (sourceDrawer) {
        this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
        return;
      }

      const hasLineItems = Boolean(
        doc.querySelector('.vrv-drawer__items .vrv-cart-line') ||
          doc.querySelector('.vrv-drawer__items [role="list"] > *')
      );
      if (hasLineItems) {
        this.classList.remove('is-empty');
        return;
      }
    }

    if (
      parsedState.id ||
      parsedState.variant_id ||
      parsedState.key ||
      (Array.isArray(parsedState.items) && parsedState.items.length > 0)
    ) {
      this.classList.remove('is-empty');
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsed ? parsed.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  clearPrefetchCache() {
    this._vrvPrefetchedDrawerHtml = null;
    this._vrvPrefetchedAt = 0;
  }

  refreshDrawer(forceFresh = false) {
    this._refreshDrawerSeq = (this._refreshDrawerSeq || 0) + 1;
    const refreshSeq = this._refreshDrawerSeq;

    const couponsPageWasOpen = this.classList.contains('is-coupons-open');
    const wasEmpty = this.classList.contains('is-empty');
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    if (forceFresh) {
      this.clearPrefetchCache();
    }

    const prefetched =
      !forceFresh &&
      this._vrvPrefetchedDrawerHtml &&
      this._vrvPrefetchedAt &&
      Date.now() - this._vrvPrefetchedAt < 5000
        ? this._vrvPrefetchedDrawerHtml
        : null;

    if (prefetched) {
      this._vrvPrefetchedDrawerHtml = null;
      this._vrvPrefetchedAt = 0;
    }

    const fetchPromise = prefetched
      ? Promise.resolve(prefetched)
      : fetch(`${cartUrl}?section_id=cart-drawer${forceFresh ? '&_t=' + Date.now() : ''}`, {
          method: 'GET',
          headers: { Accept: 'text/html' },
          cache: 'no-store',
          credentials: 'same-origin',
        }).then((response) => response.text());

    return fetchPromise
      .then((responseText) => {
        if (refreshSeq !== this._refreshDrawerSeq) return;
        const doc = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceDrawer = doc.querySelector('cart-drawer');
        const sourceInner = doc.querySelector('#CartDrawer .drawer__inner');
        const liveInner = this.querySelector('#CartDrawer .drawer__inner');

        const sourceLineCount = sourceInner
          ? sourceInner.querySelectorAll('.vrv-drawer__items .vrv-cart-line').length
          : null;

        let shouldBeEmpty = wasEmpty;
        if (sourceDrawer) {
          shouldBeEmpty = sourceDrawer.classList.contains('is-empty');
        } else if (sourceLineCount !== null) {
          shouldBeEmpty = sourceLineCount === 0;
        }

        if (sourceLineCount === 0) {
          shouldBeEmpty = true;
        } else if (sourceLineCount > 0) {
          shouldBeEmpty = false;
        }

        if (shouldBeEmpty) {
          this.classList.add('is-empty');
          this.classList.remove('is-coupons-open');
          const couponsPage = document.getElementById('CartDrawer-CouponsPage');
          if (couponsPage) {
            couponsPage.classList.remove('is-open');
            couponsPage.setAttribute('hidden', '');
            couponsPage.setAttribute('aria-hidden', 'true');
          }
        } else {
          this.classList.remove('is-empty');
        }

        if (sourceInner && liveInner) {
          liveInner.innerHTML = sourceInner.innerHTML;
        }

        if (couponsPageWasOpen && !this.classList.contains('is-empty')) {
          const newPage = document.getElementById('CartDrawer-CouponsPage');
          if (newPage) {
            newPage.removeAttribute('hidden');
            newPage.classList.add('is-open');
            newPage.setAttribute('aria-hidden', 'false');
          }
          this.classList.add('is-coupons-open');
        }

        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) {
          overlay.addEventListener('click', this.close.bind(this));
        }

        this.setHeaderCartIconAccessibility();
        this._vrvLastRefreshAt = Date.now();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'refreshDrawer' } }));
      });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer-Header',
        section: 'cart-drawer',
        selector: '.vrv-drawer__count',
      },
      {
        id: 'CartDrawer-CartItems',
        section: 'cart-drawer',
        selector: '.vrv-drawer__items',
      },
      {
        id: 'CartDrawer-FooterTotals',
        section: 'cart-drawer',
        selector: '#CartDrawer-FooterTotals',
      },
      {
        id: 'CartDrawer-Pinned',
        section: 'cart-drawer',
        selector: '#CartDrawer-Pinned',
      },
      {
        id: 'CartDrawer-CheckoutBar',
        section: 'cart-drawer',
        selector: '#CartDrawer-CheckoutBar',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
